import {
  FOC_TOUR_LABEL_RES_MODEL,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_RES_MODEL_TOUR_HREF as HREF } from '../focTourHrefs';
import {
  FOC_POTENTIAL_DISPOSITION,
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
  focResTourPopoverHtml,
  requirement,
  type FocResTourStepDef,
} from './focResTourTypes';

export const FOC_RES_MODEL_TOUR_PARAM = 'focResModelTour';
export const FOC_RES_MODEL_TOUR_HREF = HREF;
export { focResTourPopoverHtml };

export const FOC_RES_MODEL_TOUR_STEPS: FocResTourStepDef[] = [
  {
    id: 'sectionIntro',
    surface: 'assets',
    tour: 'resources-workspace',
    title: FOC_TOUR_LABEL_RES_MODEL,
    description:
      'PRATUS manages personnel, equipment, supplies, teams, facilities, vehicles, aircraft, vessels, contractors, and services as resources—requested, tracked, assigned, checked in/out, and demobilized from one authoritative model.',
  },
  requirement({
    id: 'res001',
    requirementId: 'FOC-RES-001',
    name: 'Use of “Resources” Terminology',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'assets',
    tour: 'resources-workspace',
    description:
      'PRATUS uses “resources” as the primary term for personnel, equipment, supplies, teams, facilities, vehicles, aircraft, vessels, contractors, services, and other incident capabilities. The data model may retain distinct subtypes such as assets, personnel, equipment, facilities, and supplies.',
  }),
  requirement({
    id: 'rs001',
    requirementId: 'US-RS001',
    name: 'Access Resources Tab',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'assets',
    tour: 'hub-tab-resources',
    description:
      'Select the Resources (Assets) tab from the main Hub navigation to view and manage available resources.',
  }),
  requirement({
    id: 'rs002',
    requirementId: 'US-RS002',
    name: 'View All Resources in List View',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'assets',
    tour: 'resources-assets-list',
    description:
      'See a comprehensive list of all resources for an overview of inventory.',
  }),
  requirement({
    id: 'rs003',
    requirementId: 'US-RS003',
    name: 'View Personnel as Resources',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'assets',
    tour: 'resources-assets-list',
    description:
      'Personnel appear as resources so staffing availability can be tracked alongside other capabilities.',
  }),
  requirement({
    id: 'rs004',
    requirementId: 'US-RS004',
    name: 'View Personnel Availability',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'assets',
    tour: 'resources-availability',
    description:
      'View availability status for personnel so you know who can be assigned.',
  }),
  requirement({
    id: 'rs015',
    requirementId: 'US-RS015',
    name: 'Check In/Out Resources',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'assets',
    tour: 'resources-check-in-out',
    description:
      'Formally check out and check in resources so custody is tracked.',
  }),
  requirement({
    id: 'rs021',
    requirementId: 'US-RS021',
    name: 'Link Resources to Incidents',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'assets',
    tour: 'resources-assets-list',
    description:
      'Resources assigned to incidents stay linked so incident records stay complete.',
  }),
  requirement({
    id: 'res006',
    requirementId: 'FOC-RES-006',
    name: 'End-to-End Resource Lifecycle',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'assets',
    tour: 'resources-workspace',
    description:
      'PRATUS manages resources as a lifecycle from need through request, approval, sourcing, ordering, ETA, arrival, check-in, assignment, accountability, demobilization, check-out, and closeout—one authoritative incident record with linked history.',
    bullets: [
      'FOC-RES-006a — Status model: requested/pending, ordered, en route, arrived, available, assigned, out of service, demobilizing, released, returned, closed.',
      'FOC-RES-006b — ETA and arrival visible to Resources, Logistics, Operations, requester, and authorized COP views.',
      'FOC-RES-006c — 201-to-resource continuity without duplicate entry at escalation.',
      'FOC-RES-006d — Cross-module sync to roster/org chart, ICS forms, COP, Logistics, Finance/Admin, and demobilization.',
    ],
  }),
  requirement({
    id: 'res007',
    requirementId: 'FOC-RES-007',
    name: 'ICS 219/T-Card Resource Status Representation',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'assets',
    tour: 'resources-tcard',
    description:
      'Resource-status representation aligned to Coast Guard ICS 219/T-card concepts for scanning, filtering, grouping, and status management—digital tables/cards may supplement a T-card view while preserving approved resource-kind meaning.',
  }),
  requirement({
    id: 'res008',
    requirementId: 'FOC-RES-008',
    name: 'DSCA Mission Assignment / e-Contingency Replacement Concept',
    disposition: FOC_POTENTIAL_DISPOSITION,
    description:
      'Stakeholder feedback identified a potential need for FEMA Mission Assignment / e-Contingency tracking. This is not direct FOC developer tasking until the Government determines scope, system-of-record, data ownership, cybersecurity, and integration.',
    bullets: [
      'FOC-RES-008a — Approved Mission Assignment intake (if activated).',
      'FOC-RES-008b — Routing and tracking to responsible Coast Guard organizations (if activated).',
      'FOC-RES-008c — AI may draft OPORD/tasking products but must not independently issue or approve official tasking.',
    ],
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_RES_MODEL),
  },
];

export function focResModelTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focResModelTourStepIds(): string[] {
  return FOC_RES_MODEL_TOUR_STEPS.map((step) => step.id);
}
